﻿import { getTranslations } from "../../smulders/translations";

export enum RequirementIconBehaviour
{
    /* 
    Show unset icon initially.
    Update to success icon when requirement is met.
    Update to error icon only when icon success has been shown and requirement is no longer met.
    */
    Default,
    /*
    Show unset icon initially.
    Update to success icon when requirement is met.
    Update to error icon when requirement is not met and field is not empty.
    */
    ValidateNotEmpty,
    /*
    Show error icon initially.
    Update to success icon when requirement is met.
    Update to error icon when requirement is not met.
    */
    ValidateImmediately
};

export class PasswordStrengthOptions
{
    passwordField?: string;
    confirmPasswordField?: string;
    customClass?: string;
    customInfoClass?: string;

    minCharLength?: number;
    maxCharLength?: number;
    specialChars?: string;
    minSpecialCharsAmount?: number;
    minDigitsAmount?: number;
    minCapitalsAmount?: number;
    minLowercaseAmount?: number;

    minRecomLength?: number;
    minRecomDigitsAmount?: number;
    minRecomCapitalsAmount?: number;
    minRecomLowercaseAmount?: number;
    minRecomSpecialCharsAmount?: number;

    hideMaxLengthOnStart?: boolean;
    wasLonger?: boolean;
    disableSubmitButton?: string;
    useDynamicInfoText?: boolean;
    useCapslockWarning?: boolean;
    showStrengthText?: boolean;
    showProgressBar?: boolean;
    showPwndInfo?: boolean;

    passwordStrengthText?: string;
    passwordStrengthsText?: Record<string, string>;

    capslockTooltip?: string;
    minCharText?: string;
    maxCharText?: string;
    combinedMinMaxText?: string;
    reqDigitText?: string;
    reqCapitalText?: string;
    reqLowerCaseText?: string;
    reqSpecialCharText?: string;
    confirmPasswordText?: string;
    pwndPasswordText?: string;

    infoTemplateClass?: string;
    infoTemplateText?: string;
    infoFieldTemplate?: string;

    iconBehaviour?: RequirementIconBehaviour;
};

export class PasswordStrengthConfig implements PasswordStrengthOptions
{
    passwordField = ".password-check";
    confirmPasswordField = ".confirm-password-check";
    customClass = "";
    customInfoClass = "";

    minCharLength = 8;
    maxCharLength = 50;
    specialChars = "(!@#$)";
    minSpecialCharsAmount = 1;
    minDigitsAmount = 1;
    minCapitalsAmount = 1;
    minLowercaseAmount = 1;

    minRecomLength = 12;
    minRecomDigitsAmount = 2;
    minRecomCapitalsAmount = 2;
    minRecomLowercaseAmount = 2;
    minRecomSpecialCharsAmount = 2;

    hideMaxLengthOnStart = false;
    wasLonger = false;
    disableSubmitButton = "";
    useDynamicInfoText = true;
    useCapslockWarning = true;
    showStrengthText = true;
    showProgressBar = true;
    showPwndInfo = false;

    passwordStrengthText = "Wachtwoord sterkte: {0}";
    passwordStrengthsText: Record<string, string> = {
        "": "zeer zwak",
        "bar-1": "zeer zwak",
        "bar-2": "zwak",
        "bar-3": "sterk",
        "bar-4": "zeer sterk",
    };

    capslockTooltip = ".tooltiptext";
    minCharText = getTranslations().minCharTxt;
    maxCharText = getTranslations().maxCharTxt;
    combinedMinMaxText = "";
    reqDigitText = getTranslations().reqDigitTxt;
    reqCapitalText = getTranslations().reqCapitalTxt;
    reqLowerCaseText = getTranslations().reqLowerCaseTxt;
    reqSpecialCharText = getTranslations().reqSpecialCharactersTxt;
    confirmPasswordText = getTranslations().confirmPasswordTxt;
    usePwnPasswordCheck = false;
    pwndPasswordText =
        "Komt niet voor in <a href=\"https://haveibeenpwned.com/\" target=\"_blank\">publieke datalekken</a>";

    infoTemplateClass = "password-strength-info";
    infoTemplateText = "";
    infoFieldTemplate =
        "<div class=\"password-strength-info-item {0} {2}\">{1}</div>";

    iconBehaviour = RequirementIconBehaviour.Default;

    constructor(overrides?: Partial<PasswordStrengthOptions>)
    {
        Object.assign(this, overrides);
    }
}

export default class EstrategyPassword
{
    options: PasswordStrengthOptions;
    passwordFieldElement: HTMLInputElement;
    confirmPasswordElement?: HTMLInputElement | null;
    ratingField: HTMLElement;
    ratingTextField: HTMLElement;
    infoField: HTMLElement;
    capslockHasEventListener: boolean;
    timeout: any;

    constructor(selector: HTMLInputElement | string, options: PasswordStrengthOptions)
    {
        if (selector instanceof HTMLElement)
            this.passwordFieldElement = selector;
        else
            options.passwordField = selector;

        if (!options.passwordField)
        {
            return;
        }
        this.options = new PasswordStrengthConfig(options);

        this.init();
    }

    init()
    {
        if (!this.passwordFieldElement)
        {
            this.passwordFieldElement = document.querySelector(
                this.options.passwordField,
            );
        }

        if (this.passwordFieldElement == null)
        {
            return;
        }

        if (this.options.confirmPasswordField != null)
        {
            this.confirmPasswordElement = document.querySelector(
                this.options.confirmPasswordField,
            );
        }

        this.capslockHasEventListener = false;

        this.SetHTML();

        this.setEvents();
        if (this.passwordFieldElement.value.length > 0)
        {
            this.checkPasswordStrength(this.passwordFieldElement);
        }
    }

    SetHTML()
    {
        // document.head.innerHTML += styleTemplate;
        const strengthBar = document.createElement("div");
        strengthBar.className = "password-strength";
        const strengthTextField = document.createElement("div");
        strengthTextField.className = "password-strength-text";
        const customClassElement = this.options.customClass ? document.querySelector(this.options.customClass) : null;
        if (this.options.showStrengthText)
        {
            if (this.options.customClass.length > 0 && customClassElement != null) customClassElement.appendChild(strengthTextField);
            else
            {
                this.passwordFieldElement.parentNode.insertBefore(
                    strengthTextField,
                    this.passwordFieldElement.nextSibling,
                );
            }
        }
        if (this.options.showProgressBar)
        {
            if (this.options.customClass.length > 0 && customClassElement != null) customClassElement.appendChild(strengthBar);
            else
            {
                this.passwordFieldElement.parentNode.insertBefore(
                    strengthBar,
                    this.passwordFieldElement.nextSibling,
                );
            }
            strengthBar.innerHTML
                += "<span class=\"password-strength-bar-1\"></span>"
                + "<span class=\"password-strength-bar-2\"></span>"
                + "<span class=\"password-strength-bar-3\"></span>"
                + "<span class=\"password-strength-bar-4\"></span>"
                + "<span class=\"password-strength-bar-rating\"></span>";
        }
        this.ratingField = strengthBar;
        this.ratingTextField = strengthTextField;

        if (this.options.disableSubmitButton)
        {
            const submutElement = document.querySelector(
                this.options.disableSubmitButton,
            );
            if (submutElement != null) submutElement.setAttribute("disabled", "");
        }

        var initialIcon = "unset";
        if (this.options.iconBehaviour == RequirementIconBehaviour.ValidateImmediately)
            initialIcon = "error";

        // Add text to html
        if (this.options.useDynamicInfoText)
        {
            const infoText = document.createElement("div");
            infoText.className = this.options.infoTemplateClass;
            infoText.innerHTML = this.options.infoTemplateText;

            const resultItemTemplate = this.options.infoFieldTemplate;

            // Hide min and max  character text when using the combined minMaxText
            if (this.options.combinedMinMaxText.length === 0)
            {
                if (this.options.minCharLength > 0)
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        initialIcon,
                        String.format(this.options.minCharText, this.options.minCharLength),
                        "min-length",
                    );
                }
                if (
                    this.options.maxCharLength > 0
                    && !this.options.hideMaxLengthOnStart
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        initialIcon,
                        String.format(this.options.maxCharText, this.options.maxCharLength),
                        "max-length",
                    );
                }
            }
            if (this.options.minDigitsAmount > 0)
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    String.format(
                        this.options.reqDigitText,
                        this.options.minDigitsAmount,
                    ),
                    "has-digit",
                );
            }
            if (this.options.minCapitalsAmount > 0)
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    String.format(
                        this.options.reqCapitalText,
                        this.options.minCapitalsAmount,
                    ),
                    "has-capital",
                );
            }
            if (this.options.minLowercaseAmount > 0)
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    String.format(
                        this.options.reqLowerCaseText,
                        this.options.minLowercaseAmount,
                    ),
                    "has-small",
                );
            }
            if (
                this.options.specialChars.length > 0
                && this.options.minSpecialCharsAmount > 0
            )
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    String.format(
                        this.options.reqSpecialCharText,
                        this.options.minSpecialCharsAmount,
                        this.options.specialChars,
                    ),
                    "one-special",
                );
            }
            
            if (
                this.options.confirmPasswordField &&
                this.confirmPasswordElement
            )
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    this.options.confirmPasswordText,
                    "confirm-pass",
                );
            }
            if (this.options.showPwndInfo)
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    initialIcon,
                    this.options.pwndPasswordText,
                    "pwnd-pass",
                );
            }
            // Show combined minMaxText
            if (this.options.combinedMinMaxText.length > 0)
            {
                infoText.innerHTML += String.format(
                    resultItemTemplate,
                    "",
                    this.options.combinedMinMaxText,
                    "combined-text",
                );
            }

            // show seperate from progress bar
            if (this.options.customInfoClass !== "")
            {
                const CustomInfoClassElement = document.querySelector(
                    this.options.customInfoClass,
                );
                if (
                    this.options.customInfoClass.length > 0
                    && CustomInfoClassElement != null
                )
                {
                    CustomInfoClassElement.appendChild(infoText);
                } else if (this.options.showProgressBar)
                {
                    strengthBar.parentNode.insertBefore(
                        infoText,
                        strengthBar.nextSibling,
                    );
                } else
                {

                    if (
                        this.options.customClass.length > 0
                        && customClassElement != null
                    )
                        customClassElement.appendChild(infoText);
                    else
                    {
                        this.passwordFieldElement.parentNode.insertBefore(
                            infoText,
                            this.passwordFieldElement.nextSibling,
                        );
                    }
                }
            }

            if (customClassElement)
                customClassElement.appendChild(infoText);
            else
            {
                this.passwordFieldElement.parentNode.insertBefore(
                    infoText,
                    this.passwordFieldElement.nextSibling,
                );
            }

            this.infoField = infoText;
        }
    }

    setEvents()
    {
        const that = this;
        this.passwordFieldElement.addEventListener("keyup", (event) =>
        {
            that.onKeyUp(event, that.passwordFieldElement);
        });

        // run same functionality for second password field
        if (this.options.confirmPasswordField != null)
        {
            if (this.confirmPasswordElement != null)
            {
                this.confirmPasswordElement.addEventListener("keyup", (event) =>
                {
                    that.onKeyUp(event, that.passwordFieldElement);
                });
            }
        }
    }

    async getSha1(string)
    {
        const buffer = new TextEncoder().encode(string);
        const buffer_1 = await crypto.subtle.digest("SHA-1", buffer);
        // Get the hex code
        const hexCodes = [];
        const view = new DataView(buffer_1);
        for (let i = 0; i < view.byteLength; i += 4)
        {
            const stringValue = view.getUint32(i).toString(16);
            const padding = "00000000";
            const paddedValue = (padding + stringValue).slice(-padding.length);
            hexCodes.push(paddedValue);
        }
        return hexCodes.join("");
    }

    onKeyUp(event, el)
    {
        this.checkPasswordStrength(el);
        if (this.options.useCapslockWarning) this.checkForCapslock(event, el);
    }

    checkPasswordStrength(el)
    {
        const currentPassword = el.value;

        const confirmPassword = this.confirmPasswordElement ? this.confirmPasswordElement.value : null;

        // password requirements for disabling buttons
        let meetsRequirements = true;
        if (currentPassword.length < this.options.minCharLength) meetsRequirements = false;
        if (
            currentPassword.length > this.options.maxCharLength
            && this.options.maxCharLength !== 0
        ) meetsRequirements = false;
        if (this.options.minDigitsAmount > 0)
        {
            if (
                currentPassword.match(/\d/g) != null
                && this.options.minDigitsAmount > currentPassword.match(/\d/g).length
            ) meetsRequirements = false;
        }
        if (this.options.minCapitalsAmount > 0)
        {
            if (
                currentPassword.match(/[A-Z]/g) == null
                || this.options.minCapitalsAmount > currentPassword.match(/[A-Z]/g).length
            ) meetsRequirements = false;
        }
        if (this.options.minCapitalsAmount > 0)
        {
            if (
                currentPassword.match(/[a-z]/g) == null
                || this.options.minCapitalsAmount > currentPassword.match(/[a-z]/g).length
            ) meetsRequirements = false;
        }

        // password scoring
        let score = 0;

        if (currentPassword.length > 0) score += 20;
        if (
            currentPassword.match(/[0-9]/g) != null
            && currentPassword.match(/[0-9]/g).length
            >= this.options.minRecomDigitsAmount
        ) score += 20;
        if (currentPassword.length >= this.options.minRecomLength) score += 20;

        if (
            this.options.specialChars.length > 0
            && this.options.minRecomSpecialCharsAmount > 0
        )
        {
            if (
                currentPassword.match(/[a-z]/g) != null
                && currentPassword.match(/[a-z]/g).length
                >= this.options.minRecomLowercaseAmount
                && currentPassword.match(/[A-Z]/g) != null
                && currentPassword.match(/[A-Z]/g).length
                >= this.options.minRecomCapitalsAmount
            ) score += 20;

            if (
                currentPassword.match(
                    new RegExp(`[${this.options.specialChars}]`, "g"),
                ) != null
                && currentPassword.match(new RegExp(`[${this.options.specialChars}]`, "g"))
                    .length >= this.options.minRecomSpecialCharsAmount
            ) score += 20;
        } else
        {
            if (
                currentPassword.match(/[a-z]/g) != null
                && currentPassword.match(/[a-z]/g).length
                >= this.options.minRecomLowercaseAmount
            ) score += 20;

            if (
                currentPassword.match(/[A-Z]/g) != null
                && currentPassword.match(/[A-Z]/g).length
                >= this.options.minRecomCapitalsAmount
            ) score += 20;
        }

        if (score === 100)
        {
            if (
                this.options.maxCharLength > 0
                && currentPassword.length > this.options.maxCharLength
            ) score -= 20;
        }

        // score bars
        let passwordRating = "";
        if (score >= 20) passwordRating = "bar-1";
        if (score >= 60 || meetsRequirements) passwordRating = "bar-2";
        if (score >= 80) passwordRating = "bar-3";
        if (score === 100) passwordRating = "bar-4";

        const strengthBar = String.format(
            "<span class=\"password-strength-bar-1\"></span>"
            + "<span class=\"password-strength-bar-2\"></span>"
            + "<span class=\"password-strength-bar-3\"></span>"
            + "<span class=\"password-strength-bar-4\"></span>"
            + "<span class=\"password-strength-bar-rating {0}\"></span>",
            passwordRating,
        );

        this.ratingTextField.textContent = String.format(
            this.options.passwordStrengthText,
            this.options.passwordStrengthsText[passwordRating],
        );
        this.ratingField.innerHTML = strengthBar;

        if (meetsRequirements && this.options.disableSubmitButton)
        {
            document
                .querySelector(this.options.disableSubmitButton)
                .removeAttribute("disabled");
        }

        // Add info text to html

        if (this.options.useDynamicInfoText)
        {
            const infoText = document.createElement("div");
            infoText.className = this.options.infoTemplateClass;
            infoText.innerHTML = this.options.infoTemplateText;

            const resultItemTemplate = this.options.infoFieldTemplate;

            var validateNotEmpty = false;
            if (this.options.iconBehaviour == RequirementIconBehaviour.ValidateNotEmpty)
                validateNotEmpty = true;

            // Hide min and max  character text when using the combined minMaxText
            if (this.options.combinedMinMaxText.length === 0)
            {
                // Minimal password length
                if (this.options.minCharLength > 0)
                {
                    const formattedMinCharText = String.format(
                        this.options.minCharText,
                        this.options.minCharLength,
                    );
                    const minCharLengthField = this.infoField.querySelector(".min-length");
                    if (currentPassword.length >= this.options.minCharLength)
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "success",
                            formattedMinCharText,
                            "min-length",
                        );
                    } else if (
                        validateNotEmpty
                        || minCharLengthField.classList.contains("success")
                        || minCharLengthField.classList.contains("error")
                    )
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "error",
                            formattedMinCharText,
                            "min-length",
                        );
                    } else
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "unset",
                            formattedMinCharText,
                            "min-length",
                        );
                    }
                }

                // Maximal password length
                if (currentPassword.length > this.options.maxCharLength) this.options.wasLonger = true;

                if (
                    this.options.maxCharLength > 0
                    && (!this.options.hideMaxLengthOnStart || this.options.wasLonger)
                )
                {
                    const formattedmaxCharText = String.format(
                        this.options.maxCharText,
                        this.options.maxCharLength,
                    );
                    const maxCharLengthField = this.infoField.querySelector(".max-length");
                    if (
                        currentPassword.length > 0
                        && currentPassword.length <= this.options.maxCharLength
                    )
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "success",
                            formattedmaxCharText,
                            "max-length",
                        );
                    } else if (
                        validateNotEmpty 
                        || maxCharLengthField.classList.contains("success")
                        || maxCharLengthField.classList.contains("error")
                        || this.options.hideMaxLengthOnStart
                    )
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "error",
                            formattedmaxCharText,
                            "max-length",
                        );
                    } else
                    {
                        infoText.innerHTML += String.format(
                            resultItemTemplate,
                            "unset",
                            formattedmaxCharText,
                            "max-length",
                        );
                    }
                }
            }

            // show if enough digits
            if (this.options.minDigitsAmount > 0)
            {
                const formattedreqDigitText = String.format(
                    this.options.reqDigitText,
                    this.options.minDigitsAmount,
                );
                const mixDigitsAmountField = this.infoField.querySelector(".has-digit");
                if (
                    currentPassword.match(/[0-9]/g) != null
                    && this.options.minDigitsAmount <= currentPassword.match(/[0-9]/g).length
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        formattedreqDigitText,
                        "has-digit",
                    );
                } else if (
                    validateNotEmpty
                    || mixDigitsAmountField.classList.contains("success")
                    || mixDigitsAmountField.classList.contains("error")
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        formattedreqDigitText,
                        "has-digit",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        formattedreqDigitText,
                        "has-digit",
                    );
                }
            }

            // show if enough capitals
            if (this.options.minCapitalsAmount > 0)
            {
                const formattedreqCapitalText = String.format(
                    this.options.reqCapitalText,
                    this.options.minCapitalsAmount,
                );
                const minCapitalsAmountField = document.querySelector(".has-capital");
                if (
                    currentPassword.match(/[A-Z]/g) != null
                    && this.options.minCapitalsAmount
                    <= currentPassword.match(/[A-Z]/g).length
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        formattedreqCapitalText,
                        "has-capital",
                    );
                } else if (
                    validateNotEmpty
                    || minCapitalsAmountField.classList.contains("success")
                    || minCapitalsAmountField.classList.contains("error")
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        formattedreqCapitalText,
                        "has-capital",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        formattedreqCapitalText,
                        "has-capital",
                    );
                }
            }

            // show if enough small characters
            if (this.options.minLowercaseAmount > 0)
            {
                const formattedreqLowerCaseText = String.format(
                    this.options.reqLowerCaseText,
                    this.options.minLowercaseAmount,
                );
                const minLowercaseAmountField = document.querySelector(".has-small");
                if (
                    currentPassword.match(/[a-z]/g) != null
                    && this.options.minLowercaseAmount
                    <= currentPassword.match(/[a-z]/g).length
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        formattedreqLowerCaseText,
                        "has-small",
                    );
                } else if (
                    validateNotEmpty
                    || minLowercaseAmountField.classList.contains("success")
                    || minLowercaseAmountField.classList.contains("error")
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        formattedreqLowerCaseText,
                        "has-small",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        formattedreqLowerCaseText,
                        "has-small",
                    );
                }
            }

            if (
                this.options.specialChars.length > 0
                && this.options.minSpecialCharsAmount > 0
            )
            {
                const formattedReqSpexialCaseText = String.format(
                    this.options.reqSpecialCharText,
                    this.options.minSpecialCharsAmount,
                    this.options.specialChars,
                );
                const specialCharsField = document.querySelector(".one-special");
                if (
                    currentPassword.match(
                        new RegExp(`[${this.options.specialChars}]`, "g"),
                    ) != null
                    && currentPassword.match(
                        new RegExp(`[${this.options.specialChars}]`, "g"),
                    ).length >= this.options.minSpecialCharsAmount
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        formattedReqSpexialCaseText,
                        "one-special",
                    );
                } else if (
                    validateNotEmpty
                    || specialCharsField.classList.contains("success")
                    || specialCharsField.classList.contains("error")
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        formattedReqSpexialCaseText,
                        "one-special",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        formattedReqSpexialCaseText,
                        "one-special",
                    );
                }
            }

            if (
                this.options.confirmPasswordField
                && this.confirmPasswordElement
            )
            {
                if (currentPassword === "" && confirmPassword === "")
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        this.options.confirmPasswordText,
                        "confirm-pass",
                    );
                    // eslint-disable-next-line eqeqeq
                } else if (confirmPassword == currentPassword)
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        this.options.confirmPasswordText,
                        "confirm-pass",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        this.options.confirmPasswordText,
                        "confirm-pass",
                    );
                }
            }

            if (this.options.showPwndInfo)
            {
                if (currentPassword !== "")
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "loading",
                        this.options.pwndPasswordText,
                        "pwnd-pass-check",
                    );
                    // run only once after 500 miliseconds of not typing
                    if (this.timeout)
                    {
                        clearTimeout(this.timeout);
                        this.timeout = null;
                    }
                    this.timeout = setTimeout(
                        this.checkHaveIBeenPwnd,
                        500,
                        this,
                        currentPassword,
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        this.options.pwndPasswordText,
                        "pwnd-pass-check",
                    );
                }
            }

            // Show combined minMaxText
            if (this.options.combinedMinMaxText.length > 0)
            {
                // Minimal password length
                const combinedMinMaxTextField = document.querySelector(".combined-text");
                if (
                    currentPassword.length >= this.options.minCharLength
                    && currentPassword.length <= this.options.maxCharLength
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "success",
                        this.options.combinedMinMaxText,
                        "combined-text",
                    );
                } else if (
                    combinedMinMaxTextField.classList.contains("success")
                    || combinedMinMaxTextField.classList.contains("error")
                )
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "error",
                        this.options.combinedMinMaxText,
                        "combined-text",
                    );
                } else
                {
                    infoText.innerHTML += String.format(
                        resultItemTemplate,
                        "unset",
                        this.options.combinedMinMaxText,
                        "combined-text",
                    );
                }
            }

            const infoFieldElement = document.getElementsByClassName(
                this.options.infoTemplateClass,
            )[0];
            infoFieldElement.parentNode.replaceChild(infoText, infoFieldElement);
            this.infoField = infoText;
        }
    }

    checkHaveIBeenPwnd(that, currentPassword)
    {
        const pwnedElement = document.querySelector(".pwnd-pass-check");
        const xhttp = new XMLHttpRequest();
        that.getSha1(currentPassword).then((passHash) =>
        {
            xhttp.open(
                "GET",
                `https://api.pwnedpasswords.com/range/${passHash.substring(0, 5)}`,
                true,
            );
            xhttp.send();
            xhttp.onload = function (result: any)
            {
                const resultPassHash = passHash.slice(5).toUpperCase();
                let isPwned = false;

                result.target.response.split("\n").some((hash) =>
                {
                    if (hash.split(":")[0] === resultPassHash)
                    {
                        isPwned = true;
                        return true; // Stop iteration if match found
                    }
                    return false; // Continue iteration if match not found
                });

                if (isPwned)
                {
                    pwnedElement.classList.remove("loading");
                    pwnedElement.classList.add("error");
                } else
                {
                    pwnedElement.classList.remove("loading");
                    pwnedElement.classList.add("success");
                }
            };
            xhttp.onerror = function (err)
            {
                pwnedElement.classList.remove("loading");
                pwnedElement.classList.add("api-error");
            };
        });
        clearTimeout(this.timeout);
    }

    // Example:
    // <div class="password-strength-tooltip">
    //     <input id="password-field" />
    //     <span class="tooltiptext">Caps Lock staat aan</span>
    // </div>

    checkForCapslock(event, _el)
    {
        const capslockTooltipElement: HTMLElement = document.querySelector(
            this.options.capslockTooltip,
        );
        if (capslockTooltipElement == null)
        {
            return; // Ensure a return value even if condition is not met
        }

        if (
            !(
                typeof event.getModifierState === "function"
                && event.getModifierState("CapsLock")
            )
        )
        {
            capslockTooltipElement.style.display = "none";
            return; // Ensure a return value even if condition is not met
        }
        capslockTooltipElement.style.display = "block";

        if (this.capslockHasEventListener) return;

        document.addEventListener("click", (e) =>
        {
            // If user clicks inside the element, do nothing
            if ((e.target as HTMLElement).closest(this.options.capslockTooltip)) return;
            // If user clicks outside the element, hide it!
            capslockTooltipElement.style.display = "none";
        });
        this.capslockHasEventListener = true;
    }
}

if (typeof String.format !== "function")
{
    String.format = function (...args)
    {
        let s = args[0];

        for (let i = 0; i < args.length - 1; i++)
        {
            const reg = new RegExp(`\\{${i}\\}`, "gm");
            s = s.replace(reg, args[i + 1]);
        }

        return s;
    };
}
